import { Button, Card, Input, Popconfirm, Select, Space, Table, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import LmsStatusTag from '@/pages/education/components/LmsStatusTag';
import { EducationSectionSelect } from '@/pages/education/components/EducationLookupSelects';
import { LMS_HOME_PATH, lmsErrorMessage, useLmsAccess } from '@/pages/education/utils/lmsAccess';

type Announcement = {
  id: string; title: string; status: string; published_at?: string | null; section_name?: string; grade_code?: string;
};

export default function ClassAnnouncementListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { canManage } = useLmsAccess();
  const q = params.get('q') || '';
  const sectionId = params.get('section_id') || undefined;
  const status = params.get('status') || undefined;
  const list = useQuery({
    queryKey: ['education-lms-announcements', q, sectionId, status],
    queryFn: async () => (await api.get<Announcement[]>('/education/lms/announcements', {
      params: { q: q || undefined, section_id: sectionId, status },
    })).data || [],
  });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['education-lms-announcements'] });
    queryClient.invalidateQueries({ queryKey: ['education-lms-summary'] });
  };
  const publish = useMutation({
    mutationFn: (id: string) => api.post(`/education/lms/announcements/${id}/publish`),
    onSuccess: () => { message.success(t('lmsPublishedOk', { defaultValue: 'Published' })); refresh(); },
    onError: (e) => message.error(lmsErrorMessage(e, 'Unable to publish announcement')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/education/lms/announcements/${id}`),
    onSuccess: refresh,
    onError: (e) => message.error(lmsErrorMessage(e, 'Unable to delete announcement')),
  });
  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    setParams(next);
  };

  return (
    <EducationPageChrome
      title={t('lmsAnnouncements', { defaultValue: 'Class announcements' })}
      description={t('lmsAnnouncementsIntro', { defaultValue: 'Section-level notices.' })}
      extra={<Space>
        <Button onClick={() => navigate(LMS_HOME_PATH)}>{t('lmsBackToHub', { defaultValue: 'Back to classroom' })}</Button>
        {canManage ? (
          <Button type="primary" onClick={() => navigate('/education/lms/announcements/new')}>
            {t('lmsNewAnnouncement', { defaultValue: 'New announcement' })}
          </Button>
        ) : null}
      </Space>}
    >
      <Card size="small">
        <Space wrap style={{ marginBottom: 16 }}>
          <Input.Search
            allowClear defaultValue={q} style={{ width: 260 }}
            placeholder={t('search', { defaultValue: 'Search' })}
            onSearch={(value) => setFilter('q', value.trim())}
          />
          <EducationSectionSelect allowClear style={{ width: 240 }} value={sectionId} onChange={(v) => setFilter('section_id', v)} />
          <Select
            allowClear value={status} style={{ width: 160 }}
            placeholder={t('lmsAllStatuses', { defaultValue: 'All statuses' })}
            onChange={(v) => setFilter('status', v)}
            options={[
              { value: 'draft', label: t('lmsDraft', { defaultValue: 'Draft' }) },
              { value: 'published', label: t('lmsPublished', { defaultValue: 'Published' }) },
            ]}
          />
        </Space>
        <Table
          rowKey="id" size="small" loading={list.isLoading} dataSource={list.data}
          pagination={{ pageSize: 25 }}
          locale={{ emptyText: t('lmsNoRows', { defaultValue: 'Nothing here yet' }) }}
          columns={[
            { title: t('title', { defaultValue: 'Title' }), dataIndex: 'title' },
            { title: t('section', { defaultValue: 'Section' }), render: (_: unknown, r: Announcement) => [r.grade_code, r.section_name].filter(Boolean).join(' / ') },
            { title: t('status', { defaultValue: 'Status' }), dataIndex: 'status', width: 110, render: (v: string) => <LmsStatusTag status={v} /> },
            { title: t('lmsPublished', { defaultValue: 'Published' }), dataIndex: 'published_at', width: 130, render: (v?: string | null) => (v ? String(v).slice(0, 10) : '—') },
            {
              title: t('actions', { defaultValue: 'Actions' }), width: 240,
              render: (_: unknown, r: Announcement) => canManage ? (
                <Space>
                  <Button size="small" onClick={() => navigate(`/education/lms/announcements/${r.id}/edit`)}>{t('edit', { defaultValue: 'Edit' })}</Button>
                  {r.status === 'draft' ? (
                    <Button size="small" type="primary" loading={publish.isPending} onClick={() => publish.mutate(r.id)}>
                      {t('lmsPublish', { defaultValue: 'Publish' })}
                    </Button>
                  ) : null}
                  <Popconfirm title={t('lmsDelete', { defaultValue: 'Delete' })} onConfirm={() => remove.mutate(r.id)}>
                    <Button size="small" danger>{t('lmsDelete', { defaultValue: 'Delete' })}</Button>
                  </Popconfirm>
                </Space>
              ) : null,
            },
          ]}
        />
      </Card>
    </EducationPageChrome>
  );
}
